import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Nav, Navbar, NavDropdown } from "react-bootstrap";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import { DropdownItemProps } from "react-bootstrap/DropdownItem";

const NavbarLogo = styled.img<{
  $style: { height?: string; marginTop?: string; marginBottom?: string } | null;
}>`
  height: ${({ theme, $style }) =>
    $style && $style.height ? $style.height : theme.navbar.logo.height};
  margin-top: ${({ theme, $style }) =>
    $style && $style.marginTop
      ? $style.marginTop
      : theme.navbar.logo.marginTop};
  margin-bottom: ${({ theme, $style }) =>
    $style && $style.marginBottom
      ? $style.marginBottom
      : theme.navbar.logo.marginBottom};
  cursor: pointer;
`;

const StyledNavbar = styled(Navbar)`
  color: ${({ theme }) => theme.navbar.color};
  background-color: ${({ theme }) => theme.navbar.backgroundColor};
  border-bottom-color: ${({ theme }) => theme.navbar.border.color};
  border-bottom-width: ${({ theme }) => theme.navbar.border.width};
  border-bottom-style: solid;
  display: flex;
  justify-content: space-between;
`;

const StyledNavbarToggler = styled(Navbar.Toggle)`
  color: ${({ theme }) => theme.navbar.color};

  &:active,
  :focus {
    border: none;
    outline: none;
    color: ${({ theme }) => theme.navbar.color};
  }
`;

const StyledWrap = styled.div`
  & .nav-link {
    color: ${({ theme }) => theme.navbar.color} !important;
    cursor: pointer;
    padding: 0.5rem;

    &:hover {
      color: ${({ theme }) => theme.navbar.link.hover.color} !important;
      background-color: ${({ theme }) =>
        theme.navbar.link.hover.backgroundColor};
    }
  }

  & .nav-text {
    color: ${({ theme }) => theme.navbar.color} !important;
    cursor: pointer;

    &:hover {
      color: ${({ theme }) => theme.navbar.link.hover.color} !important;
      background-color: ${({ theme }) =>
        theme.navbar.link.hover.backgroundColor};
    }
  }

  & .active {
    color: ${({ theme }) => theme.navbar.link.hover.color} !important;
    background-color: ${({ theme }) => theme.navbar.link.hover.backgroundColor};
  }

  & .navbar-toggler {
    border-color: transparent;
    color: ${({ theme }) => theme.navbar.color};

    &:hover {
      color: ${({ theme }) => theme.navbar.link.hover.color} !important;
      background-color: ${({ theme }) =>
        theme.navbar.link.hover.backgroundColor};
    }
  }

  & .navbar-collapse {
    flex-grow: 0;
  }

  & .dropdown-menu {
    border-radius: 0;
    cursor: pointer;
    max-height: 75vh;
    overflow-y: auto;
  }

  & .dropdown-item {
    cursor: pointer;

    &:hover {
      color: ${({ theme }) => theme.navbar.link.hover.color} !important;
      background-color: ${({ theme }) =>
        theme.navbar.link.hover.backgroundColor};
    }
  }
`;

const NonCollapsableItemsWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const LogoWithItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`;

interface NavBarProps {
  logo: string;
  logohandler: Function;
  items: { url?: string; label: string; handler?: Function }[];
  nonCollapsableItems: {
    url?: string;
    label: JSX.Element | string;
    handler?: Function;
  }[] | null;
  dropdown?: {
    selected: string;
    dropdownitems: {
      label: string;
      id: string | number;
      handler: Function;
    }[];
  };
  logoStyle: {
    height?: string;
    marginTop?: string;
    marginBottom?: string;
  } | null;
}

const NavBar = ({
  logo,
  logohandler,
  items = [],
  nonCollapsableItems = [],
  dropdown,
  logoStyle = null,
}: NavBarProps) => {
  const [collapsed, setCollapsed] = useState(true);

  const toggleNavbar = () => {
    setCollapsed(!collapsed);
  };

  const handleClickLink = (e: React.MouseEvent, handler: Function) => {
    setCollapsed(true);
    handler(e);
  };

  const handleMenuClick = (
    id: string | number,
    e: React.MouseEvent<DropdownItemProps, MouseEvent>,
    handler: Function
  ) => {
    handler(id, e);
  };

  if (dropdown) {
    dropdown?.dropdownitems?.sort((a, b) => {
      const labelA = a.label.toUpperCase();
      const labelB = b.label.toUpperCase();
      if (labelA < labelB) {
        return -1;
      }
      if (labelA > labelB) {
        return 1;
      }
      return 0;
    });
  }

  return (
    <StyledWrap>
      <StyledNavbar expand="lg" fixed="top">
        <LogoWithItemWrapper>
          <Navbar.Brand
            onClick={(e: React.MouseEvent) => logohandler && logohandler(e)}
          >
            {logo ? (
              <NavbarLogo alt="Logo" src={logo} $style={logoStyle} />
            ) : (
              ""
            )}
          </Navbar.Brand>
          <NonCollapsableItemsWrapper>
            {nonCollapsableItems?.map(({ label, url, handler }, index) => (
              <Nav.Item key={index}>
                <Navbar.Text
                  className="nav-text"
                  onClick={(e: React.MouseEvent) =>
                    handler && handleClickLink(e, handler)
                  }
                >
                  {label}
                </Navbar.Text>
              </Nav.Item>
            ))}
          </NonCollapsableItemsWrapper>
        </LogoWithItemWrapper>
        <StyledNavbarToggler onClick={toggleNavbar}>
          <FontAwesomeIcon icon={collapsed ? faBars : faTimes} />
        </StyledNavbarToggler>
        <Navbar.Collapse>
          <Nav navbar className="ml-auto">
            {dropdown && (
              <NavDropdown
                id="navbar-dropdown"
                title={dropdown.selected}
                alignRight={true}
              >
                {dropdown?.dropdownitems?.map(({ label, id, handler }) => (
                  <NavDropdown.Item
                    key={id}
                    onClick={(
                      e: React.MouseEvent<DropdownItemProps, MouseEvent>
                    ) => handler && handleMenuClick(id, e, handler)}
                  >
                    {label}
                  </NavDropdown.Item>
                ))}
              </NavDropdown>
            )}
            {items.map(({ label, url, handler }) => {
              return (
                <Nav.Item key={label}>
                  {url && !url.startsWith("http") ? (
                    <NavLink
                      className="nav-link"
                      to={url}
                      onClick={(e: React.MouseEvent) => handler && handleClickLink(e, handler)}
                    >
                      {label}
                    </NavLink>
                  ) : (
                    <Nav.Link
                      onClick={(e: React.MouseEvent) =>
                        handler && handleClickLink(e, handler)
                      }
                      href={url}
                    >
                      {label}
                    </Nav.Link>
                  )}
                </Nav.Item>
              );
            })}
          </Nav>
        </Navbar.Collapse>
      </StyledNavbar>
    </StyledWrap>
  );
};

export default NavBar;
